import React from 'react';
import { motion } from 'framer-motion';
import { Flame, AlertTriangle, BookOpen, ArrowRight } from 'lucide-react';
const fadeIn = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: 'easeOut'
    }
  }
};
export function StorySections() {
  const scrollToOffer = () => {
    document.getElementById('offer')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  return <div className="bg-bbq-dark text-gray-300 py-12 md:py-24 px-4 relative" data-id="element-199">
      <div className="max-w-3xl mx-auto space-y-12 md:space-y-24" data-id="element-200">
        {/* SECCIÓN 1: EL GANCHO */}
        <motion.section id="hook" initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={fadeIn} className="relative" data-id="element-201">
          <div className="absolute -left-8 top-0 text-bbq-surface opacity-50" data-id="element-202">
            <Flame size={120} data-id="element-203" />
          </div>
          <h2 className="text-3xl md:text-4xl font-display font-bold text-white mb-6 uppercase tracking-wide" data-id="element-204">
            ¿Te ha pasado que llega el domingo...
          </h2>
          <div className="prose prose-invert prose-lg max-w-none text-gray-300 leading-relaxed" data-id="element-205">
            <p data-id="element-206">
              ...todos están sentados a la mesa, y el asado que hizo tu papá
              está tan duro que parece neumático? Todos dicen "está rico, papá"
              por compromiso, pero tú ves a los niños peleando con el tenedor y
              los trozos de carne quedando de lado en el plato.
            </p>
            <p data-id="element-207">
              Es una situación incómoda, frustrante y, sobre todo, una pérdida
              de dinero en cortes que terminan secos. Pero, ¿y si este Día del
              Padre pudieras regalarle el secreto para que nunca más vuelva a
              pasar vergüenza frente a la parrilla?
            </p>
          </div>
        </motion.section>

        {/* SECCIÓN 2: AGITACIÓN DEL DOLOR */}
        <motion.section initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={fadeIn} className="bg-bbq-surface p-8 md:p-10 rounded-xl border border-white/5 relative overflow-hidden" data-id="element-208">
          <div className="absolute -right-4 -bottom-4 text-bbq-dark opacity-50" data-id="element-209">
            <AlertTriangle size={160} data-id="element-210" />
          </div>
          <h2 className="text-3xl md:text-4xl font-display font-bold text-bbq-ember mb-6 uppercase tracking-wide relative z-10" data-id="element-211">
            Seamos honestos: regalarle a un papá chileno es difícil.
          </h2>
          <div className="prose prose-invert prose-lg max-w-none text-gray-300 leading-relaxed relative z-10" data-id="element-212">
            <p data-id="element-213">
              Ya tiene mil corbatas, herramientas que no usa y perfumes que se
              llenan de polvo. Pero hay algo que a casi todos les apasiona:{' '}
              <strong data-id="element-214">El asado.</strong>
            </p>
            <p data-id="element-215">
              El problema es que la mayoría de los papás parrillean "por
              instinto". No conocen la técnica del atemperado, no saben qué sal
              usar y terminan gastando una fortuna en Lomo Vetado porque creen
              que es la única forma de que la carne quede blanda.
            </p>
            <p className="text-white font-medium" data-id="element-216">
              El resultado es frustración, humo innecesario y carnes
              "arrebatadas" que arruinan el almuerzo familiar más importante de
              la semana.
            </p>
            <p className="italic border-l-4 border-bbq-ember pl-4 mt-6" data-id="element-217">
              ¿Realmente quieres regalarle otra billetera de cuero este año? ¿O
              prefieres darle la habilidad de ser admirado por toda la familia
              en cada asado?
            </p>
          </div>
        </motion.section>

        {/* CTA SECUNDARIO */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={fadeIn} className="flex justify-center -mt-12" data-id="element-218">
          <button onClick={scrollToOffer} className="group inline-flex items-center gap-2 text-bbq-gold hover:text-bbq-ember font-medium text-base md:text-lg border-b border-bbq-gold/30 hover:border-bbq-ember pb-1 transition-colors duration-300" data-id="element-219">
            Ver el Pack Completo por $14.990
            <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true" data-id="element-220" />
          </button>
        </motion.div>

        {/* SECCIÓN 3: LA REVELACIÓN */}
        <motion.section initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={fadeIn} data-id="element-221">
          <div className="flex items-center gap-4 mb-6" data-id="element-222">
            <BookOpen className="text-bbq-gold" size={40} data-id="element-223" />
            <h2 className="text-3xl md:text-4xl font-display font-bold text-white uppercase tracking-wide" data-id="element-224">
              La Solucion
            </h2>
          </div>
          <div className="prose prose-invert prose-lg max-w-none text-gray-300 leading-relaxed" data-id="element-225">
            <p data-id="element-226">
              Después de años analizando por qué los asados chilenos fallan,
              descubrimos que no es culpa de la carne del supermercado, ni de la
              calidad del carbón. El problema es la falta de un{' '}
              <strong data-id="element-227">Método diseñado y probado por profesionales</strong>.
            </p>
            <div className="bg-gradient-to-r from-bbq-surface to-bbq-dark p-6 rounded-lg border-l-4 border-bbq-gold my-8" data-id="element-228">
              <h3 className="text-2xl font-display font-bold text-bbq-gold mb-3 uppercase" data-id="element-229">
                Presentamos "La Biblia del Asado Chileno"
              </h3>
              <p className="mb-0" data-id="element-230">
                No es solo un libro de recetas, es un sistema de supervivencia
                parrillera diseñado específicamente para nuestra cultura,
                nuestros cortes y nuestro bolsillo.
              </p>
            </div>
            <p data-id="element-231">
              Es el manual que enseña a transformar una sobrecostilla de oferta
              en un manjar que se corta con el tenedor. Es el regalo que papá no
              sabía que necesitaba, pero que no podrá dejar de usar.
            </p>
          </div>
        </motion.section>
      </div>
    </div>;
}